/**
 * Protocole du hall (menu principal) : serveurs, parties, amis, présence et invitations.
 *
 * Même WebSocket que le jeu (/ws, même routeur Traefik), mais un autre premier message :
 *   client  lobby{token}             -> serveur
 *   serveur lobby-welcome{me, social, stats}
 *   client  req{id, req}             -> serveur res{id, ok, data | error}
 *   serveur social{social}           état social complet, à chaque changement (amis, demandes, invitations)
 *   serveur notice{n}                fait ponctuel à signaler (demande reçue, invitation...)
 *   serveur servers{stats}           la liste des serveurs a changé : le client la redemande s'il l'affiche
 *   client  ping -> serveur pong
 *
 * Le jeu garde son protocole (protocol.ts). Le client garde le hall ouvert pendant la partie :
 * la présence et les invitations restent en direct. Aucune donnée n'est sondée en HTTP.
 */
import type { PosteRole } from './state';
import { MAPS } from './map';

export const LOBBY_VERSION = 1;

/** public : listé pour tous ; friends : listé pour les amis du créateur ; private : sur invitation ou code. */
export type WorldVisibility = 'public' | 'friends' | 'private';
export const WORLD_VISIBILITIES: readonly WorldVisibility[] = ['public', 'friends', 'private'];
export const MAX_PLAYERS_CHOICES: readonly number[] = [2, 4, 8, 16, 32];
export const DEFAULT_MAX_PLAYERS = 8;
/** Parties qu'un joueur peut posséder en même temps. */
export const MAX_OWNED_WORLDS = 10;
export const MAX_FRIENDS = 200;
export const MAX_PENDING_REQUESTS = 50;
/** Durée de validité d'une invitation (ms). */
export const INVITE_TTL_MS = 12 * 3600 * 1000;

/** Nom d'une partie ou d'une station : 3 à 32 caractères, commence par une lettre ou un chiffre. */
export const WORLD_NAME_RE = /^[\p{L}\p{N}][\p{L}\p{N} _'’.,!&()-]{2,31}$/u;

export interface PlayerRef {
  id: string;
  name: string;
}

export interface LobbyMe extends PlayerRef {
  kind: 'guest' | 'user';
}

export interface PosteSummary {
  role: PosteRole;
  name: string;
  controller: 'player' | 'ai';
  playerName: string | null;
}

/** Une partie vue depuis le menu : ce qu'il faut pour choisir, sans charger le monde. */
export interface ServerSummary {
  id: string;
  name: string;
  mapId: string;
  visibility: WorldVisibility;
  maxPlayers: number;
  owner: PlayerRef | null;
  stationName: string;
  /** Joueurs connectés en ce moment. */
  online: PlayerRef[];
  /** Comptes qui ont déjà joué sur ce monde. */
  members: number;
  /** Monde en marche (au moins un joueur) ; sinon en veille, le temps y est arrêté. */
  live: boolean;
  /** Horloge de jeu (s) : jour et date se déduisent avec dayIndex / calendarDay. */
  gameTime: number;
  reputation: number;
  satisfaction: number;
  visitorsToday: number;
  openKm: number;
  lifts: number;
  pistes: number;
  postes: PosteSummary[];
  createdAt: number;
  savedAt: number;
  /** Mon rôle si j'ai déjà joué sur ce monde. */
  myRole: 'station' | 'observer' | null;
  lastPlayedAt: number | null;
  /** Amis qui y ont déjà joué ou qui y sont connectés. */
  friends: PlayerRef[];
  /** Invitation reçue pour ce monde (son expéditeur). */
  invitedBy: PlayerRef | null;
}

export type Presence =
  | { state: 'offline'; lastSeen: number | null }
  | { state: 'menu' }
  /** world vaut null si la partie n'est pas visible pour moi (privée). */
  | { state: 'game'; world: { id: string; name: string } | null };

export interface FriendInfo extends PlayerRef {
  kind: 'guest' | 'user';
  since: number;
  presence: Presence;
}

export interface FriendRequestInfo extends PlayerRef {
  at: number;
}

export interface InviteInfo {
  id: number;
  from: PlayerRef;
  world: { id: string; name: string; stationName: string };
  at: number;
  expiresAt: number;
}

export interface SocialState {
  friends: FriendInfo[];
  incoming: FriendRequestInfo[];
  outgoing: FriendRequestInfo[];
  invites: InviteInfo[];
  blocked: PlayerRef[];
}

export interface RecentPlayer extends PlayerRef {
  at: number;
  worldName: string;
}

export interface LobbyStats {
  playersOnline: number;
  worldsLive: number;
}

export const EMPTY_SOCIAL: SocialState = { friends: [], incoming: [], outgoing: [], invites: [], blocked: [] };

/* ------------------------------------------------------------------ */
/* Requêtes                                                             */
/* ------------------------------------------------------------------ */

export type LobbyRequest =
  | { op: 'servers' }
  | { op: 'world.get'; id: string }
  | { op: 'world.create'; name: string; stationName: string; visibility: WorldVisibility; maxPlayers: number; mapId?: string }
  | { op: 'world.update'; id: string; name?: string; visibility?: WorldVisibility; maxPlayers?: number }
  | { op: 'world.delete'; id: string }
  | { op: 'world.leave'; id: string }
  | { op: 'friend.request'; name: string }
  | { op: 'friend.accept'; id: string }
  | { op: 'friend.decline'; id: string }
  | { op: 'friend.cancel'; id: string }
  | { op: 'friend.remove'; id: string }
  | { op: 'block'; id: string }
  | { op: 'unblock'; id: string }
  | { op: 'invite'; to: string; world: string }
  | { op: 'invite.dismiss'; id: number }
  | { op: 'recent' };

export type LobbyOp = LobbyRequest['op'];

/** Réponse de chaque requête en cas de succès. */
export interface LobbyResults {
  servers: { servers: ServerSummary[] };
  'world.get': { server: ServerSummary };
  'world.create': { server: ServerSummary };
  'world.update': { server: ServerSummary };
  'world.delete': Record<string, never>;
  'world.leave': Record<string, never>;
  'friend.request': { accepted: boolean };
  'friend.accept': Record<string, never>;
  'friend.decline': Record<string, never>;
  'friend.cancel': Record<string, never>;
  'friend.remove': Record<string, never>;
  block: Record<string, never>;
  unblock: Record<string, never>;
  invite: Record<string, never>;
  'invite.dismiss': Record<string, never>;
  recent: { players: RecentPlayer[] };
}

/* ------------------------------------------------------------------ */
/* Messages                                                             */
/* ------------------------------------------------------------------ */

export type LobbyClientMessage = { t: 'lobby'; v: number; token: string } | { t: 'req'; id: number; req: LobbyRequest } | { t: 'ping'; id: number; ct: number };

export type LobbyNotice =
  | { k: 'friend-request'; from: PlayerRef }
  | { k: 'friend-accepted'; by: PlayerRef }
  | { k: 'invite'; invite: InviteInfo }
  | { k: 'friend-online'; friend: PlayerRef };

export type LobbyServerMessage =
  | { t: 'lobby-welcome'; v: number; me: LobbyMe; social: SocialState; stats: LobbyStats }
  | { t: 'res'; id: number; ok: true; data: unknown }
  | { t: 'res'; id: number; ok: false; error: string }
  | { t: 'social'; social: SocialState }
  | { t: 'notice'; n: LobbyNotice }
  | { t: 'servers'; stats: LobbyStats }
  | { t: 'pong'; id: number; ct: number; st: number }
  | { t: 'error'; code: 'protocol' | 'auth' | 'rate' | 'internal'; message: string };

/* ------------------------------------------------------------------ */
/* Validation de forme (le serveur vérifie ensuite les droits)          */
/* ------------------------------------------------------------------ */

const str = (v: unknown, max: number): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;
/** Identifiant de monde (base64url) ou de compte (UUID). */
const id = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z0-9_-]{4,64}$/.test(v);
const optVisibility = (v: unknown) => v === undefined || WORLD_VISIBILITIES.includes(v as WorldVisibility);
const optPlayers = (v: unknown) => v === undefined || (typeof v === 'number' && MAX_PLAYERS_CHOICES.includes(v));

export function parseLobbyRequest(raw: unknown): LobbyRequest | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  switch (r.op) {
    case 'servers':
    case 'recent':
      return { op: r.op };
    case 'world.get':
    case 'world.delete':
    case 'world.leave':
      return id(r.id) ? { op: r.op, id: r.id } : null;
    case 'world.create': {
      if (!str(r.name, 64) || !str(r.stationName, 64) || !WORLD_VISIBILITIES.includes(r.visibility as WorldVisibility) || !optPlayers(r.maxPlayers) || r.maxPlayers === undefined) return null;
      // Carte : absente (Val Cendrée) ou l'une des cartes connues.
      if (r.mapId !== undefined && (typeof r.mapId !== 'string' || !Object.hasOwn(MAPS, r.mapId))) return null;
      const out: Extract<LobbyRequest, { op: 'world.create' }> = { op: 'world.create', name: r.name, stationName: r.stationName, visibility: r.visibility as WorldVisibility, maxPlayers: r.maxPlayers as number };
      if (r.mapId !== undefined) out.mapId = r.mapId as string;
      return out;
    }
    case 'world.update': {
      if (!id(r.id) || (r.name !== undefined && !str(r.name, 64)) || !optVisibility(r.visibility) || !optPlayers(r.maxPlayers)) return null;
      const out: Extract<LobbyRequest, { op: 'world.update' }> = { op: 'world.update', id: r.id };
      if (r.name !== undefined) out.name = r.name as string;
      if (r.visibility !== undefined) out.visibility = r.visibility as WorldVisibility;
      if (r.maxPlayers !== undefined) out.maxPlayers = r.maxPlayers as number;
      return out;
    }
    case 'friend.request':
      return str(r.name, 64) ? { op: 'friend.request', name: r.name } : null;
    case 'friend.accept':
    case 'friend.decline':
    case 'friend.cancel':
    case 'friend.remove':
    case 'block':
    case 'unblock':
      return id(r.id) ? { op: r.op, id: r.id } : null;
    case 'invite':
      return id(r.to) && id(r.world) ? { op: 'invite', to: r.to, world: r.world } : null;
    case 'invite.dismiss':
      return typeof r.id === 'number' && Number.isInteger(r.id) && r.id > 0 ? { op: 'invite.dismiss', id: r.id } : null;
    default:
      return null;
  }
}

export function parseLobbyClientMessage(raw: unknown): LobbyClientMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.t) {
    case 'lobby':
      return typeof m.v === 'number' && typeof m.token === 'string' && m.token.length < 200 ? { t: 'lobby', v: m.v, token: m.token } : null;
    case 'req': {
      if (typeof m.id !== 'number' || !Number.isInteger(m.id)) return null;
      const req = parseLobbyRequest(m.req);
      return req ? { t: 'req', id: m.id, req } : null;
    }
    case 'ping':
      return typeof m.id === 'number' && typeof m.ct === 'number' ? { t: 'ping', id: m.id, ct: m.ct } : null;
    default:
      return null;
  }
}

/** Le premier message ouvre-t-il une session de hall (et non de jeu) ? */
export function isLobbyHello(raw: unknown): boolean {
  return typeof raw === 'object' && raw !== null && (raw as { t?: unknown }).t === 'lobby';
}
